import { Box, Circle, Flex, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import type {
  ConnectionEvent,
  ConnectionEventKind,
} from '~/atoms/ConnectionState'
import { TranslationKeys } from '~/locales/i18next'

const EVENT_LABEL_KEY: Record<ConnectionEventKind, string> = {
  online: 'statusPanel.eventOnline',
  offline: 'statusPanel.eventOffline',
  'ws-connected': 'statusPanel.eventWsConnected',
  'ws-reconnecting': 'statusPanel.eventWsReconnecting',
  'ws-suspended': 'statusPanel.eventWsSuspended',
  'ws-disconnected': 'statusPanel.eventWsDisconnected',
}

// Green = recovered/healthy transition, red/orange = degraded, gray = neutral.
const EVENT_COLOR: Record<ConnectionEventKind, string> = {
  online: 'green.solid',
  offline: 'red.solid',
  'ws-connected': 'green.solid',
  'ws-reconnecting': 'orange.solid',
  'ws-suspended': 'gray.solid',
  'ws-disconnected': 'red.solid',
}

function formatTime(epochMs: number): string {
  const d = new Date(epochMs)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

type Props = {
  events: ConnectionEvent[]
}

export const ConnectionEventsList = ({ events }: Props) => {
  const { t } = useTranslation()

  if (events.length === 0) {
    return (
      <Text fontSize="sm" color="fg.muted">
        {t('statusPanel.noEvents')}
      </Text>
    )
  }

  // Newest first for readability. Track each event's original index so the
  // React key stays stable: keying on the reversed position would shift every
  // item's key when a new event is prepended, remounting the whole list.
  const ordered = events
    .map((event, originalIdx) => ({ event, originalIdx }))
    .reverse()

  // Cap the visible height so a long log scrolls within the section rather
  // than pushing the rest of the panel far down the page (~6 rows tall).
  return (
    <Box maxH="13.5rem" overflowY="auto">
      {ordered.map(({ event, originalIdx }) => (
        <Flex
          key={`${event.at}-${event.kind}-${originalIdx}`}
          align="center"
          gap={2}
          py={1}
        >
          <Circle size="0.5rem" bg={EVENT_COLOR[event.kind]} flexShrink={0} />
          <Text fontSize="sm" color="fg.default" flex={1}>
            {t(EVENT_LABEL_KEY[event.kind] as TranslationKeys)}
          </Text>
          <Text fontSize="sm" color="fg.muted" fontFamily="mono" flexShrink={0}>
            {formatTime(event.at)}
          </Text>
        </Flex>
      ))}
    </Box>
  )
}
